import React, { useState, useEffect, useCallback, useRef } from 'react';
import FullCalendar from '@fullcalendar/react';
import dayGridPlugin from '@fullcalendar/daygrid';
import timeGridPlugin from '@fullcalendar/timegrid';
import interactionPlugin from '@fullcalendar/interaction';
import listPlugin from '@fullcalendar/list';
import { planningService, machineService, orderService, productionLotService, productService, userService } from '../services/api';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import {
  Plus, Filter, X, List, CalendarDays, Trash2, Pencil, Search,
  Play, CheckCircle, AlertOctagon, Clock, ChevronLeft, ChevronRight
} from 'lucide-react';

const ACTIVITY_TYPES = [
  { value: 'produzione', label: 'Produzione', color: '#3b82f6' },
  { value: 'piazzamento', label: 'Piazzamento', color: '#f59e0b' },
  { value: 'campionatura', label: 'Campionatura', color: '#8b5cf6' },
  { value: 'manutenzione', label: 'Manutenzione', color: '#ef4444' },
];
const STATUSES = [
  { value: 'da_fare', label: 'Da fare' },
  { value: 'in_corso', label: 'In corso' },
  { value: 'completata', label: 'Completata' },
  { value: 'bloccata', label: 'Bloccata' },
];
const PRIORITIES = [
  { value: 'bassa', label: 'Bassa' },
  { value: 'normale', label: 'Normale' },
  { value: 'alta', label: 'Alta' },
  { value: 'urgente', label: 'Urgente' },
];
const getTypeColor = (type) => ACTIVITY_TYPES.find(t => t.value === type)?.color || '#6b7280';
const getStatusBadge = (s) => ({ da_fare: 'bg-blue-100 text-blue-700', in_corso: 'bg-amber-100 text-amber-700', completata: 'bg-green-100 text-green-700', bloccata: 'bg-red-100 text-red-700' })[s] || 'bg-slate-100 text-slate-600';
const getPriorityBadge = (p) => ({ bassa: 'bg-slate-100 text-slate-600', normale: 'bg-blue-100 text-blue-600', alta: 'bg-orange-100 text-orange-700', urgente: 'bg-red-100 text-red-700' })[p] || 'bg-slate-100 text-slate-600';

export default function PlanningPage() {
  const { user } = useAuth();
  const calendarRef = useRef(null);
  const [activities, setActivities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [viewMode, setViewMode] = useState('calendar');
  const [showModal, setShowModal] = useState(false);
  const [editingActivity, setEditingActivity] = useState(null);
  const [showDetail, setShowDetail] = useState(null);
  const [showFilters, setShowFilters] = useState(false);
  const [filters, setFilters] = useState({ activity_type: '', machine_id: '', department: '', status: '', priority: '', search: '' });
  const [machines, setMachines] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [orders, setOrders] = useState([]);
  const [lots, setLots] = useState([]);
  const [products, setProducts] = useState([]);
  const [users, setUsers] = useState([]);
  const [dateRange, setDateRange] = useState({ start: null, end: null });
  const [form, setForm] = useState({
    title: '', activity_type: 'produzione', order_id: '', order_line_id: '', production_lot_id: '',
    product_id: '', product_revision_id: '', machine_id: '', assigned_user_id: '',
    planned_date: '', planned_start_time: '', planned_end_time: '',
    estimated_duration_minutes: '', priority: 'normale', notes: '', color_tag: '', status: 'da_fare',
  });
  const canEdit = ['admin', 'production', 'management'].includes(user?.role);

  const fetchActivities = useCallback(async () => {
    try {
      setLoading(true);
      const params = {};
      if (dateRange.start) params.start_date = dateRange.start;
      if (dateRange.end) params.end_date = dateRange.end;
      Object.entries(filters).forEach(([k, v]) => { if (v) params[k] = v; });
      const res = await planningService.getAll(params);
      setActivities(res.data);
    } catch { toast.error('Errore caricamento attività'); } finally { setLoading(false); }
  }, [dateRange, filters]);

  useEffect(() => { fetchActivities(); }, [fetchActivities]);
  useEffect(() => {
    Promise.all([
      machineService.getAll(), machineService.getDepartments(),
      orderService.getAll(), productionLotService.getAll(),
      productService.getAll(), userService.getAll(),
    ]).then(([m, d, o, l, p, u]) => {
      setMachines(m.data); setDepartments(d.data); setOrders(o.data);
      setLots(l.data); setProducts(p.data); setUsers(u.data);
    }).catch(() => {});
  }, []);

  const calendarEvents = activities.map(act => ({
    id: act.id,
    title: act.title,
    start: act.planned_start_time,
    end: act.planned_end_time,
    backgroundColor: act.color_tag || getTypeColor(act.activity_type),
    borderColor: act.color_tag || getTypeColor(act.activity_type),
    textColor: '#fff',
    extendedProps: { ...act },
  }));

  const handleDateChange = (info) => {
    setDateRange({ start: info.startStr || info.start?.toISOString(), end: info.endStr || info.end?.toISOString() });
  };
  const resetForm = () => setForm({
    title: '', activity_type: 'produzione', order_id: '', order_line_id: '', production_lot_id: '',
    product_id: '', product_revision_id: '', machine_id: '', assigned_user_id: '',
    planned_date: '', planned_start_time: '', planned_end_time: '',
    estimated_duration_minutes: '', priority: 'normale', notes: '', color_tag: '', status: 'da_fare',
  });
  const openCreateModal = (startStr, endStr) => {
    setEditingActivity(null); resetForm();
    if (startStr) {
      const dateOnly = startStr.slice(0, 10);
      const sTime = startStr.length > 10 ? startStr.slice(0, 16) : `${dateOnly}T08:00`;
      const eTime = endStr ? (endStr.length > 10 ? endStr.slice(0, 16) : `${endStr.slice(0, 10)}T17:00`) : `${dateOnly}T12:00`;
      setForm(f => ({ ...f, planned_date: dateOnly, planned_start_time: sTime, planned_end_time: eTime }));
    }
    setShowModal(true);
  };
  const openEditModal = (act) => {
    setEditingActivity(act); setShowDetail(null);
    setForm({
      title: act.title || '', activity_type: act.activity_type || 'produzione',
      order_id: act.order_id || '', order_line_id: act.order_line_id || '',
      production_lot_id: act.production_lot_id || '', product_id: act.product_id || '',
      product_revision_id: act.product_revision_id || '', machine_id: act.machine_id || '',
      assigned_user_id: act.assigned_user_id || '',
      planned_date: act.planned_date || '',
      planned_start_time: act.planned_start_time ? act.planned_start_time.slice(0, 16) : '',
      planned_end_time: act.planned_end_time ? act.planned_end_time.slice(0, 16) : '',
      estimated_duration_minutes: act.estimated_duration_minutes || '',
      priority: act.priority || 'normale', notes: act.notes || '',
      color_tag: act.color_tag || '', status: act.status || 'da_fare',
    });
    setShowModal(true);
  };
  const handleDateSelect = (selectInfo) => {
    if (!canEdit) return;
    openCreateModal(selectInfo.startStr, selectInfo.endStr);
  };
  const handleEventClick = (clickInfo) => {
    const act = clickInfo.event.extendedProps;
    setShowDetail({ ...act, id: clickInfo.event.id });
  };
  const handleEventDrop = async (info) => {
    if (!canEdit) { info.revert(); return; }
    try {
      const newStart = info.event.startStr;
      const newEnd = info.event.endStr || info.event.startStr;
      await planningService.update(info.event.id, {
        planned_start_time: newStart, planned_end_time: newEnd,
        planned_date: newStart.slice(0, 10),
      });
      toast.success('Attività spostata'); fetchActivities();
    } catch { info.revert(); toast.error('Errore spostamento'); }
  };
  const handleEventResize = async (info) => {
    if (!canEdit) { info.revert(); return; }
    try {
      await planningService.update(info.event.id, {
        planned_start_time: info.event.startStr, planned_end_time: info.event.endStr,
      });
      toast.success('Durata aggiornata'); fetchActivities();
    } catch { info.revert(); toast.error('Errore'); }
  };
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.title || !form.planned_start_time || !form.planned_end_time) {
      toast.error('Compila titolo, data inizio e data fine'); return;
    }
    try {
      const payload = { ...form };
      Object.keys(payload).forEach(k => { if (payload[k] === '' || payload[k] === null) payload[k] = null; });
      payload.title = form.title; payload.activity_type = form.activity_type;
      payload.planned_start_time = form.planned_start_time; payload.planned_end_time = form.planned_end_time;
      payload.planned_date = form.planned_date || form.planned_start_time.slice(0, 10);
      payload.priority = form.priority || 'normale';
      if (form.estimated_duration_minutes) payload.estimated_duration_minutes = parseInt(form.estimated_duration_minutes);
      else delete payload.estimated_duration_minutes;
      if (editingActivity) {
        await planningService.update(editingActivity.id, payload);
        toast.success('Attività aggiornata');
      } else {
        delete payload.status;
        await planningService.create(payload);
        toast.success('Attività creata');
      }
      setShowModal(false); fetchActivities();
    } catch (err) { toast.error(err.response?.data?.detail || 'Errore'); }
  };
  const handleDelete = async (id) => {
    if (!window.confirm('Eliminare questa attività?')) return;
    try { await planningService.delete(id); toast.success('Eliminata'); setShowModal(false); setShowDetail(null); fetchActivities(); } catch { toast.error('Errore'); }
  };
  const quickStatusChange = async (id, newStatus) => {
    try {
      await planningService.update(id, { status: newStatus });
      toast.success(`Stato aggiornato: ${STATUSES.find(s => s.value === newStatus)?.label}`);
      setShowDetail(null); fetchActivities();
    } catch { toast.error('Errore'); }
  };
  const clearFilters = () => setFilters({ activity_type: '', machine_id: '', department: '', status: '', priority: '', search: '' });
  const hasActiveFilters = Object.values(filters).some(v => v);

  const renderEventContent = (eventInfo) => (
    <div className="px-1 py-0.5 text-xs leading-tight overflow-hidden cursor-pointer" data-testid={`calendar-event-${eventInfo.event.id}`}>
      <div className="font-semibold truncate">{eventInfo.event.title}</div>
      {eventInfo.event.extendedProps.machine_name && <div className="opacity-80 truncate">{eventInfo.event.extendedProps.machine_name}</div>}
    </div>
  );

  // Group activities by machine for the machine-row view
  const machineGroups = {};
  const unassigned = [];
  activities.forEach(a => {
    if (a.machine_id && a.machine_name) {
      if (!machineGroups[a.machine_id]) machineGroups[a.machine_id] = { name: a.machine_name, code: a.machine_code, activities: [] };
      machineGroups[a.machine_id].activities.push(a);
    } else {
      unassigned.push(a);
    }
  });

  return (
    <div className="space-y-4" data-testid="planning-page">
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-slate-900" data-testid="planning-title">Pianificazione Generale</h1>
          <p className="text-sm text-slate-500 mt-0.5">{activities.length} attività</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <div className="flex bg-slate-100 rounded-lg p-0.5">
            {[{ key: 'calendar', label: 'Calendario', icon: CalendarDays }, { key: 'machines', label: 'Per Macchina', icon: List }, { key: 'list', label: 'Lista', icon: List }].map(v => (
              <button key={v.key} onClick={() => setViewMode(v.key)} data-testid={`view-${v.key}-btn`}
                className={`px-3 py-1.5 text-sm font-medium rounded-md transition-colors ${viewMode === v.key ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500 hover:text-slate-700'}`}>
                <v.icon className="w-4 h-4 inline mr-1" />{v.label}
              </button>
            ))}
          </div>
          <Button variant="outline" size="sm" onClick={() => setShowFilters(!showFilters)} data-testid="toggle-filters-btn"
            className={hasActiveFilters ? 'border-blue-300 bg-blue-50 text-blue-700' : ''}>
            <Filter className="w-4 h-4 mr-1" />Filtri{hasActiveFilters && <span className="ml-1 bg-blue-500 text-white rounded-full w-4 h-4 text-xs flex items-center justify-center">{Object.values(filters).filter(v => v).length}</span>}
          </Button>
          {canEdit && <Button size="sm" onClick={() => openCreateModal()} data-testid="new-activity-btn"><Plus className="w-4 h-4 mr-1" />Nuova Attività</Button>}
        </div>
      </div>

      {/* Legend */}
      <div className="flex items-center gap-4 flex-wrap">
        {ACTIVITY_TYPES.map(t => (
          <div key={t.value} className="flex items-center gap-1.5 text-sm">
            <div className="w-3 h-3 rounded-sm" style={{ backgroundColor: t.color }} /><span className="text-slate-600">{t.label}</span>
          </div>
        ))}
      </div>

      {/* Filters */}
      {showFilters && (
        <div className="bg-white border border-slate-200 rounded-xl p-4" data-testid="filters-panel">
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
            <div><label className="block text-xs font-medium text-slate-500 mb-1">Cerca</label><div className="relative"><Search className="w-4 h-4 absolute left-2.5 top-2.5 text-slate-400" /><input type="text" placeholder="Cerca..." value={filters.search} onChange={e => setFilters(f => ({ ...f, search: e.target.value }))} className="w-full pl-8 pr-3 py-2 text-sm border border-slate-200 rounded-lg" data-testid="filter-search" /></div></div>
            <div><label className="block text-xs font-medium text-slate-500 mb-1">Tipo</label><select value={filters.activity_type} onChange={e => setFilters(f => ({ ...f, activity_type: e.target.value }))} className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg" data-testid="filter-type"><option value="">Tutti</option>{ACTIVITY_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}</select></div>
            <div><label className="block text-xs font-medium text-slate-500 mb-1">Macchina</label><select value={filters.machine_id} onChange={e => setFilters(f => ({ ...f, machine_id: e.target.value }))} className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg" data-testid="filter-machine"><option value="">Tutte</option>{machines.map(m => <option key={m.id} value={m.id}>{m.machine_code} - {m.machine_name}</option>)}</select></div>
            <div><label className="block text-xs font-medium text-slate-500 mb-1">Reparto</label><select value={filters.department} onChange={e => setFilters(f => ({ ...f, department: e.target.value }))} className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg" data-testid="filter-department"><option value="">Tutti</option>{departments.map(d => <option key={d} value={d}>{d}</option>)}</select></div>
            <div><label className="block text-xs font-medium text-slate-500 mb-1">Stato</label><select value={filters.status} onChange={e => setFilters(f => ({ ...f, status: e.target.value }))} className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg" data-testid="filter-status"><option value="">Tutti</option>{STATUSES.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}</select></div>
            <div><label className="block text-xs font-medium text-slate-500 mb-1">Priorità</label><select value={filters.priority} onChange={e => setFilters(f => ({ ...f, priority: e.target.value }))} className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg" data-testid="filter-priority"><option value="">Tutte</option>{PRIORITIES.map(p => <option key={p.value} value={p.value}>{p.label}</option>)}</select></div>
          </div>
          {hasActiveFilters && <div className="mt-3 flex justify-end"><button onClick={clearFilters} className="text-sm text-blue-600 hover:text-blue-800 font-medium" data-testid="clear-filters-btn">Resetta filtri</button></div>}
        </div>
      )}

      {/* Calendar View */}
      {viewMode === 'calendar' && (
        <div className="bg-white border border-slate-200 rounded-xl p-3 sm:p-4 planning-calendar" data-testid="calendar-view">
          <FullCalendar
            ref={calendarRef}
            plugins={[dayGridPlugin, timeGridPlugin, interactionPlugin, listPlugin]}
            initialView="timeGridWeek"
            headerToolbar={{ left: 'prev,next today', center: 'title', right: 'dayGridMonth,timeGridWeek,timeGridDay,listWeek' }}
            buttonText={{ today: 'Oggi', month: 'Mese', week: 'Settimana', day: 'Giorno', list: 'Lista' }}
            locale="it" firstDay={1} allDaySlot={true} allDayText="Tutto il giorno"
            slotMinTime="06:00:00" slotMaxTime="22:00:00" slotDuration="00:30:00"
            height="auto" contentHeight={650} events={calendarEvents}
            editable={canEdit} selectable={canEdit} selectMirror={true}
            select={handleDateSelect} eventClick={handleEventClick}
            eventDrop={handleEventDrop} eventResize={handleEventResize}
            eventContent={renderEventContent} datesSet={handleDateChange}
            nowIndicator={true} businessHours={{ daysOfWeek: [1, 2, 3, 4, 5], startTime: '07:00', endTime: '19:00' }}
            eventDisplay="block"
          />
        </div>
      )}

      {/* Machine Row View */}
      {viewMode === 'machines' && (
        <div className="space-y-3" data-testid="machine-view">
          {Object.keys(machineGroups).length === 0 && unassigned.length === 0 ? (
            <div className="bg-white border border-slate-200 rounded-xl p-8 text-center text-slate-400"><CalendarDays className="w-12 h-12 mx-auto mb-3 text-slate-300" /><p className="font-medium text-slate-500">Nessuna attività trovata</p></div>
          ) : (
            <>
              {Object.entries(machineGroups).map(([mId, group]) => (
                <div key={mId} className="bg-white border border-slate-200 rounded-xl overflow-hidden">
                  <div className="px-4 py-3 bg-slate-50 border-b flex items-center gap-2">
                    <div className="w-2 h-8 bg-slate-700 rounded-full" />
                    <div><p className="text-sm font-bold text-slate-900">{group.name}</p><p className="text-xs text-slate-500 font-mono">{group.code}</p></div>
                    <span className="ml-auto text-xs bg-slate-200 text-slate-600 px-2 py-0.5 rounded-full">{group.activities.length} attività</span>
                  </div>
                  <div className="divide-y divide-slate-50">
                    {group.activities.map(a => (
                      <div key={a.id} className="px-4 py-2.5 flex items-center gap-3 hover:bg-slate-50 cursor-pointer transition-colors" onClick={() => setShowDetail(a)} data-testid={`machine-activity-${a.id}`}>
                        <div className="w-1 h-8 rounded-full" style={{ backgroundColor: a.color_tag || getTypeColor(a.activity_type) }} />
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-slate-900 truncate">{a.title}</p>
                          <p className="text-xs text-slate-500">{a.planned_date} | {a.planned_start_time?.slice(11, 16)} - {a.planned_end_time?.slice(11, 16)}</p>
                        </div>
                        <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${getStatusBadge(a.status)}`}>{STATUSES.find(s => s.value === a.status)?.label}</span>
                        <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${getPriorityBadge(a.priority)}`}>{a.priority}</span>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
              {unassigned.length > 0 && (
                <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
                  <div className="px-4 py-3 bg-slate-50 border-b"><p className="text-sm font-bold text-slate-500">Senza macchina assegnata</p></div>
                  <div className="divide-y divide-slate-50">
                    {unassigned.map(a => (
                      <div key={a.id} className="px-4 py-2.5 flex items-center gap-3 hover:bg-slate-50 cursor-pointer" onClick={() => setShowDetail(a)}>
                        <div className="w-1 h-8 rounded-full" style={{ backgroundColor: a.color_tag || getTypeColor(a.activity_type) }} />
                        <div className="flex-1 min-w-0"><p className="text-sm font-medium text-slate-900 truncate">{a.title}</p><p className="text-xs text-slate-500">{a.planned_date}</p></div>
                        <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${getStatusBadge(a.status)}`}>{STATUSES.find(s => s.value === a.status)?.label}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      )}

      {/* List View */}
      {viewMode === 'list' && (
        <div className="bg-white border border-slate-200 rounded-xl overflow-hidden" data-testid="list-view">
          {activities.length === 0 ? (
            <div className="p-8 text-center text-slate-400"><CalendarDays className="w-12 h-12 mx-auto mb-3 text-slate-300" /><p className="font-medium text-slate-500">Nessuna attività trovata</p></div>
          ) : (
            <div className="overflow-x-auto"><table className="w-full text-sm"><thead><tr className="bg-slate-50 text-left text-xs font-semibold text-slate-500 uppercase tracking-wide">
              <th className="px-4 py-3">Codice</th><th className="px-4 py-3">Titolo</th><th className="px-4 py-3">Tipo</th><th className="px-4 py-3">Macchina</th><th className="px-4 py-3">Data/Ora</th><th className="px-4 py-3">Stato</th><th className="px-4 py-3">Priorità</th><th className="px-4 py-3">Assegnato</th><th className="px-4 py-3"></th>
            </tr></thead><tbody className="divide-y divide-slate-100">
              {activities.map(act => (
                <tr key={act.id} className="hover:bg-slate-50 transition-colors cursor-pointer" onClick={() => setShowDetail(act)} data-testid={`list-activity-${act.id}`}>
                  <td className="px-4 py-3 font-mono text-xs text-slate-500">{act.activity_code}</td>
                  <td className="px-4 py-3"><p className="font-medium text-slate-900 truncate max-w-xs">{act.title}</p>{act.lot_code && <p className="text-xs text-slate-400">{act.lot_code}</p>}</td>
                  <td className="px-4 py-3"><span className="inline-flex items-center gap-1 text-xs font-medium px-2 py-0.5 rounded-full" style={{ backgroundColor: `${getTypeColor(act.activity_type)}20`, color: getTypeColor(act.activity_type) }}>{ACTIVITY_TYPES.find(t => t.value === act.activity_type)?.label}</span></td>
                  <td className="px-4 py-3 text-slate-600">{act.machine_name || '-'}</td>
                  <td className="px-4 py-3 text-slate-600 text-xs">{act.planned_date}<br />{act.planned_start_time?.slice(11, 16)} - {act.planned_end_time?.slice(11, 16)}</td>
                  <td className="px-4 py-3"><span className={`text-xs font-medium px-2 py-0.5 rounded-full ${getStatusBadge(act.status)}`}>{STATUSES.find(s => s.value === act.status)?.label}</span></td>
                  <td className="px-4 py-3"><span className={`text-xs font-medium px-2 py-0.5 rounded-full ${getPriorityBadge(act.priority)}`}>{PRIORITIES.find(p => p.value === act.priority)?.label}</span></td>
                  <td className="px-4 py-3 text-xs text-slate-500">{act.assigned_user_name || '-'}</td>
                  <td className="px-4 py-3" onClick={e => e.stopPropagation()}><div className="flex gap-1 justify-end">
                    {canEdit && <button onClick={() => openEditModal(act)} className="p-1 text-slate-400 hover:text-blue-600 rounded"><Pencil className="w-4 h-4" /></button>}
                    {user?.role === 'admin' && <button onClick={() => handleDelete(act.id)} className="p-1 text-slate-400 hover:text-red-600 rounded"><Trash2 className="w-4 h-4" /></button>}
                  </div></td>
                </tr>
              ))}
            </tbody></table></div>
          )}
        </div>
      )}

      {/* Detail Side Panel */}
      {showDetail && (
        <div className="fixed inset-0 z-50 flex justify-end bg-black/30" onClick={() => setShowDetail(null)} data-testid="detail-panel">
          <div className="bg-white w-full max-w-md h-full overflow-y-auto shadow-xl" onClick={e => e.stopPropagation()}>
            <div className="sticky top-0 bg-white border-b border-slate-200 px-6 py-4 flex items-center justify-between">
              <div><p className="text-xs font-mono text-slate-400">{showDetail.activity_code}</p><h2 className="text-lg font-bold text-slate-900">{showDetail.title}</h2></div>
              <button onClick={() => setShowDetail(null)} className="p-1.5 hover:bg-slate-100 rounded-lg"><X className="w-5 h-5" /></button>
            </div>
            <div className="p-6 space-y-5">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="text-xs font-medium px-2.5 py-1 rounded-full" style={{ backgroundColor: `${getTypeColor(showDetail.activity_type)}20`, color: getTypeColor(showDetail.activity_type) }}>{ACTIVITY_TYPES.find(t => t.value === showDetail.activity_type)?.label}</span>
                <span className={`text-xs font-medium px-2.5 py-1 rounded-full ${getStatusBadge(showDetail.status)}`}>{STATUSES.find(s => s.value === showDetail.status)?.label}</span>
                <span className={`text-xs font-medium px-2.5 py-1 rounded-full ${getPriorityBadge(showDetail.priority)}`}>{PRIORITIES.find(p => p.value === showDetail.priority)?.label}</span>
              </div>
              <div className="space-y-3 text-sm">
                {[['Macchina', showDetail.machine_name], ['Data', showDetail.planned_date],
                  ['Orario', `${showDetail.planned_start_time?.slice(11, 16)} - ${showDetail.planned_end_time?.slice(11, 16)}`],
                  ['Durata', showDetail.estimated_duration_minutes ? `${showDetail.estimated_duration_minutes} min` : null],
                  ['Ordine', showDetail.order_number], ['Lotto', showDetail.lot_code],
                  ['Prodotto', showDetail.product_name], ['Assegnato a', showDetail.assigned_user_name],
                  ['Note', showDetail.notes]].filter(([, v]) => v).map(([label, val]) => (
                  <div key={label} className="flex justify-between"><span className="text-slate-500">{label}</span><span className="text-slate-900 font-medium text-right max-w-[60%]">{val}</span></div>
                ))}
              </div>
              {/* Quick Actions */}
              {canEdit && (
                <div className="space-y-2 pt-4 border-t border-slate-200">
                  <p className="text-xs font-semibold text-slate-500 uppercase">Azioni rapide</p>
                  <div className="flex flex-wrap gap-2">
                    {showDetail.status !== 'in_corso' && <Button size="sm" variant="outline" onClick={() => quickStatusChange(showDetail.id, 'in_corso')} data-testid="action-in-corso"><Play className="w-3.5 h-3.5 mr-1" />In Corso</Button>}
                    {showDetail.status !== 'completata' && <Button size="sm" variant="outline" className="text-green-600 border-green-200 hover:bg-green-50" onClick={() => quickStatusChange(showDetail.id, 'completata')} data-testid="action-completata"><CheckCircle className="w-3.5 h-3.5 mr-1" />Completata</Button>}
                    {showDetail.status !== 'bloccata' && <Button size="sm" variant="outline" className="text-red-600 border-red-200 hover:bg-red-50" onClick={() => quickStatusChange(showDetail.id, 'bloccata')} data-testid="action-bloccata"><AlertOctagon className="w-3.5 h-3.5 mr-1" />Bloccata</Button>}
                  </div>
                  <div className="flex gap-2 mt-3">
                    <Button size="sm" onClick={() => openEditModal(showDetail)} className="flex-1" data-testid="detail-edit-btn"><Pencil className="w-3.5 h-3.5 mr-1" />Modifica</Button>
                    {user?.role === 'admin' && <Button size="sm" variant="outline" className="text-red-600 border-red-200 hover:bg-red-50" onClick={() => handleDelete(showDetail.id)} data-testid="detail-delete-btn"><Trash2 className="w-3.5 h-3.5 mr-1" />Elimina</Button>}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Create/Edit Modal */}
      {showModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" data-testid="activity-modal">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200">
              <h2 className="text-lg font-bold text-slate-900" data-testid="modal-title">{editingActivity ? 'Modifica Attività' : 'Nuova Attività'}</h2>
              <button onClick={() => setShowModal(false)} className="p-1.5 hover:bg-slate-100 rounded-lg"><X className="w-5 h-5 text-slate-500" /></button>
            </div>
            <form onSubmit={handleSubmit} className="p-6 space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                <div className="sm:col-span-2"><label className="block text-sm font-medium text-slate-700 mb-1">Titolo *</label><input type="text" value={form.title} onChange={e => setForm(f => ({ ...f, title: e.target.value }))} className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg" required data-testid="form-title" /></div>
                <div><label className="block text-sm font-medium text-slate-700 mb-1">Tipo *</label><select value={form.activity_type} onChange={e => setForm(f => ({ ...f, activity_type: e.target.value }))} className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg" data-testid="form-type">{ACTIVITY_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}</select></div>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                <div><label className="block text-sm font-medium text-slate-700 mb-1">Data *</label><input type="date" value={form.planned_date} onChange={e => setForm(f => ({ ...f, planned_date: e.target.value }))} className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg" required data-testid="form-date" /></div>
                <div><label className="block text-sm font-medium text-slate-700 mb-1">Ora Inizio *</label><input type="datetime-local" value={form.planned_start_time} onChange={e => setForm(f => ({ ...f, planned_start_time: e.target.value }))} className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg" required data-testid="form-start" /></div>
                <div><label className="block text-sm font-medium text-slate-700 mb-1">Ora Fine *</label><input type="datetime-local" value={form.planned_end_time} onChange={e => setForm(f => ({ ...f, planned_end_time: e.target.value }))} className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg" required data-testid="form-end" /></div>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div><label className="block text-sm font-medium text-slate-700 mb-1">Macchina</label><select value={form.machine_id} onChange={e => setForm(f => ({ ...f, machine_id: e.target.value }))} className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg" data-testid="form-machine"><option value="">-- Nessuna --</option>{machines.map(m => <option key={m.id} value={m.id}>{m.machine_code} - {m.machine_name}</option>)}</select></div>
                <div><label className="block text-sm font-medium text-slate-700 mb-1">Durata stimata (min)</label><input type="number" value={form.estimated_duration_minutes} onChange={e => setForm(f => ({ ...f, estimated_duration_minutes: e.target.value }))} className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg" data-testid="form-duration" /></div>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                <div><label className="block text-sm font-medium text-slate-700 mb-1">Priorità</label><select value={form.priority} onChange={e => setForm(f => ({ ...f, priority: e.target.value }))} className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg" data-testid="form-priority">{PRIORITIES.map(p => <option key={p.value} value={p.value}>{p.label}</option>)}</select></div>
                {editingActivity && <div><label className="block text-sm font-medium text-slate-700 mb-1">Stato</label><select value={form.status} onChange={e => setForm(f => ({ ...f, status: e.target.value }))} className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg" data-testid="form-status">{STATUSES.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}</select></div>}
                <div><label className="block text-sm font-medium text-slate-700 mb-1">Assegnato a</label><select value={form.assigned_user_id} onChange={e => setForm(f => ({ ...f, assigned_user_id: e.target.value }))} className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg" data-testid="form-assigned"><option value="">-- Nessuno --</option>{users.map(u => <option key={u.id} value={u.id}>{u.first_name} {u.last_name}</option>)}</select></div>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                <div><label className="block text-sm font-medium text-slate-700 mb-1">Ordine</label><select value={form.order_id} onChange={e => setForm(f => ({ ...f, order_id: e.target.value }))} className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg" data-testid="form-order"><option value="">-- Nessuno --</option>{orders.map(o => <option key={o.id} value={o.id}>{o.order_number}</option>)}</select></div>
                <div><label className="block text-sm font-medium text-slate-700 mb-1">Lotto</label><select value={form.production_lot_id} onChange={e => setForm(f => ({ ...f, production_lot_id: e.target.value }))} className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg" data-testid="form-lot"><option value="">-- Nessuno --</option>{lots.map(l => <option key={l.id} value={l.id}>{l.lot_code} - {l.product_name || ''}</option>)}</select></div>
                <div><label className="block text-sm font-medium text-slate-700 mb-1">Prodotto</label><select value={form.product_id} onChange={e => setForm(f => ({ ...f, product_id: e.target.value }))} className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg" data-testid="form-product"><option value="">-- Nessuno --</option>{products.map(p => <option key={p.id} value={p.id}>{p.code} - {p.name}</option>)}</select></div>
              </div>
              <div><label className="block text-sm font-medium text-slate-700 mb-1">Note</label><textarea value={form.notes} onChange={e => setForm(f => ({ ...f, notes: e.target.value }))} rows={2} className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg resize-none" data-testid="form-notes" /></div>
              <div className="flex items-center justify-between pt-3 border-t border-slate-200">
                <div>{editingActivity && user?.role === 'admin' && <Button type="button" variant="ghost" size="sm" onClick={() => handleDelete(editingActivity.id)} className="text-red-600 hover:text-red-700 hover:bg-red-50" data-testid="modal-delete-btn"><Trash2 className="w-4 h-4 mr-1" />Elimina</Button>}</div>
                <div className="flex gap-2"><Button type="button" variant="outline" size="sm" onClick={() => setShowModal(false)}>Annulla</Button><Button type="submit" size="sm" data-testid="modal-save-btn">{editingActivity ? 'Salva' : 'Crea Attività'}</Button></div>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
